import { Box, Button, Flex, Panel, Text } from "@coaching-culture/ui";
import { UserFlag } from "components/UserFlag";
import { ContinuousFeedbackTable } from "pages/Solutions/ContinuousFeedback/MyContinuousFeedback";
import { usePendingFeedbackRequests } from "pages/Solutions/ContinuousFeedback/PendingFeedbackRequests";
import {
  FaArrowLeft,
  FaArrowRight,
  FaHourglassHalf,
  FaReply,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { grid, GridProps } from "styled-system";
import { WidgetPanelHeader } from "./WidgetPanelHeader";

const Widget = styled(Panel)<GridProps>`
  ${grid};
`;

const ReasonText = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MAX_REQUEST_ROWS = 3;

const PendingRequestRows = () => {
  const { awaiting, toAnswer } = usePendingFeedbackRequests();

  if (awaiting.length === 0 && toAnswer.length === 0) {
    return null;
  }

  const shown = toAnswer.slice(0, MAX_REQUEST_ROWS);
  const overflow = toAnswer.length - shown.length;

  return (
    <>
      {shown.map((x) => (
        <Flex
          key={x.id}
          alignItems="center"
          px={3}
          py={2}
          borderBottom={1}
          style={{ gap: 12 }}
        >
          <Box style={{ flex: "0 0 auto" }}>
            <UserFlag user={x.requester} />
          </Box>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <ReasonText color="textMuted" title={x.reason}>
              {x.reason}
            </ReasonText>
          </Box>
          <Box style={{ flex: "0 0 auto" }}>
            <Button
              to={`/solutions/continuous-feedback/send/${x.requester.id}/${x.id}`}
              small
              icon={FaReply}
            >
              Give feedback
            </Button>
          </Box>
        </Flex>
      ))}
      {(overflow > 0 || awaiting.length > 0) && (
        <Flex
          alignItems="center"
          px={3}
          py={2}
          borderBottom={1}
          style={{ gap: 8 }}
        >
          <Text color="grey" lineHeight={0}>
            <FaHourglassHalf />
          </Text>
          <Text fontSize="small" color="textMuted">
            {overflow > 0 &&
              `${overflow} more ${
                overflow === 1 ? "person is" : "people are"
              } waiting for your feedback. `}
            {awaiting.length > 0 &&
              `You're waiting on ${awaiting.length} feedback ${
                awaiting.length === 1 ? "request" : "requests"
              } you've sent. `}
            <Link to="/solutions/continuous-feedback">View requests</Link>
          </Text>
        </Flex>
      )}
    </>
  );
};

export const FeedbackWidget = () => {
  const { awaiting, toAnswer } = usePendingFeedbackRequests();

  return (
    <Widget
      gridColumn={["span 1", "span 1", "span 1", "span 2"]}
      width={"100%"}
    >
      <WidgetPanelHeader title="Continuous feedback">
        <Button to="/solutions/continuous-feedback/send" icon={FaArrowRight}>
          Send feedback
        </Button>
        <Button to="/solutions/continuous-feedback/request" icon={FaArrowLeft}>
          Request feedback
        </Button>
      </WidgetPanelHeader>

      <PendingRequestRows />
      <ContinuousFeedbackTable
        hideWhenEmpty={awaiting.length > 0 || toAnswer.length > 0}
      />
    </Widget>
  );
};
